import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Table, Tag, Button, Input, Select, DatePicker, Space, Tooltip,
  Modal, Typography, Card, Row, Col, Statistic, Timeline, Badge, Steps,
} from "antd";
import {
  SearchOutlined, ReloadOutlined, EyeOutlined,
  GlobalOutlined, LinkOutlined, SwapRightOutlined,
  ClockCircleOutlined, FileSearchOutlined, TeamOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import ReactApexChart from "react-apexcharts";
import {
  fetchUserActivity, fetchUserActivityStats, fetchSessionPath,
} from "../../../core/data/redux/slices/UserActivitySlice";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const REFERRER_COLOR = { direct: "default", internal: "blue", external: "green" };
const REFERRER_ICON  = { direct: <GlobalOutlined />, internal: <SwapRightOutlined />, external: <LinkOutlined /> };

function fmtSeconds(s) {
  if (!s && s !== 0) return "—";
  if (s < 60)  return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

// ── Charts ────────────────────────────────────────────────────────────────────
function TopPagesBar({ data }) {
  if (!data?.length) return null;
  return (
    <ReactApexChart
      type="bar"
      height={230}
      options={{
        chart: { toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 4, horizontal: true } },
        colors: ["#6366f1"],
        xaxis: { categories: data.map((d) => d._id) },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (v) => `${v} views` } },
        grid: { borderColor: "#f1f5f9" },
      }}
      series={[{ name: "Views", data: data.map((d) => d.count) }]}
    />
  );
}

function AvgTimeBar({ data }) {
  if (!data?.length) return null;
  return (
    <ReactApexChart
      type="bar"
      height={230}
      options={{
        chart: { toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 4, horizontal: true } },
        colors: ["#f59e0b"],
        xaxis: { categories: data.map((d) => d._id), labels: { formatter: (v) => `${Math.round(v)}s` } },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (v) => fmtSeconds(Math.round(v)) } },
        grid: { borderColor: "#f1f5f9" },
      }}
      series={[{ name: "Avg Time", data: data.map((d) => Math.round(d.avgTime)) }]}
    />
  );
}

function ReferrerDonut({ data }) {
  if (!data?.length) return null;
  const colorMap = { direct: "#9ca3af", internal: "#3b82f6", external: "#22c55e" };
  return (
    <ReactApexChart
      type="donut"
      height={230}
      options={{
        labels: data.map((d) => d._id || "direct"),
        colors: data.map((d) => colorMap[d._id] || "#9ca3af"),
        legend: { position: "bottom", fontSize: "12px" },
        dataLabels: { enabled: true, formatter: (v) => `${v.toFixed(0)}%` },
        plotOptions: { pie: { donut: { size: "60%" } } },
        tooltip: { y: { formatter: (v) => `${v} visits` } },
      }}
      series={data.map((d) => d.count)}
    />
  );
}

function TopReferrersBar({ data }) {
  if (!data?.length) return (
    <div style={{ height: 230, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af" }}>
      No external referrers
    </div>
  );
  return (
    <ReactApexChart
      type="bar"
      height={230}
      options={{
        chart: { toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 4, horizontal: true } },
        colors: ["#22c55e"],
        xaxis: { categories: data.map((d) => d._id) },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (v) => `${v} visits` } },
        grid: { borderColor: "#f1f5f9" },
      }}
      series={[{ name: "Visits", data: data.map((d) => d.count) }]}
    />
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function AdminUserActivity() {
  const dispatch = useDispatch();
  const { activities, total, loading, stats, statsLoading, sessionPath, sessionPathLoading } = useSelector((s) => s.userActivity);

  const [filters, setFilters] = useState({
    page: 1, limit: 50,
    userRole: undefined, referrerType: undefined,
    search: "", startDate: undefined, endDate: undefined,
  });
  const [pathModal, setPathModal] = useState(null); // { sessionId, userName }

  const buildParams  = (s) => { const p = { ...s }; Object.keys(p).forEach((k) => { if (p[k] === undefined || p[k] === "") delete p[k]; }); return p; };
  const buildStatsP  = (s) => { const { page, limit, ...rest } = s; return buildParams(rest); };

  const load = useCallback((overrides = {}) => {
    const merged = { ...filters, ...overrides };
    dispatch(fetchUserActivity(buildParams(merged)));
  }, [dispatch, filters]);

  useEffect(() => {
    dispatch(fetchUserActivityStats({}));
    load();
  }, []);

  const updateFilter = (key, value) => {
    const updated = { ...filters, [key]: value, page: 1 };
    setFilters(updated);
    dispatch(fetchUserActivity(buildParams(updated)));
    dispatch(fetchUserActivityStats(buildStatsP(updated)));
  };

  const handleDateRange = (dates) => {
    const updated = { ...filters, startDate: dates?.[0]?.toISOString(), endDate: dates?.[1]?.toISOString(), page: 1 };
    setFilters(updated);
    dispatch(fetchUserActivity(buildParams(updated)));
    dispatch(fetchUserActivityStats(buildStatsP(updated)));
  };

  const handlePageChange = (page, pageSize) => {
    const updated = { ...filters, page, limit: pageSize };
    setFilters(updated);
    dispatch(fetchUserActivity(buildParams(updated)));
  };

  const openPathModal = (record) => {
    setPathModal({ sessionId: record.sessionId, userName: record.userName || record.userEmail });
    dispatch(fetchSessionPath(record.sessionId));
  };

  const columns = [
    {
      title: "Time",
      dataIndex: "enteredAt",
      width: 150,
      render: (v) => <span style={{ fontSize: 12, color: "#6b7280" }}>{dayjs(v).format("DD MMM YY HH:mm:ss")}</span>,
    },
    {
      title: "User",
      dataIndex: "userName",
      width: 160,
      ellipsis: true,
      render: (name, rec) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>{name || "—"}</Text>
          {rec.userEmail && <Text style={{ fontSize: 11, color: "#9ca3af" }}>{rec.userEmail}</Text>}
        </Space>
      ),
    },
    {
      title: "Role",
      dataIndex: "userRole",
      width: 110,
      render: (role) => role ? (
        <Tag color={role === "superadmin" ? "purple" : role === "companyAdmin" ? "cyan" : "default"} style={{ fontSize: 11 }}>
          {role}
        </Tag>
      ) : "—",
    },
    {
      title: "Page",
      dataIndex: "page",
      ellipsis: true,
      render: (p, rec) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12, fontFamily: "monospace" }}>{p}</Text>
          {rec.pageTitle && <Text style={{ fontSize: 11, color: "#9ca3af" }}>{rec.pageTitle}</Text>}
        </Space>
      ),
    },
    {
      title: "Time Spent",
      dataIndex: "timeSpentSeconds",
      width: 95,
      render: (s) => {
        const color = s > 300 ? "#22c55e" : s > 60 ? "#f59e0b" : "#6b7280";
        return <span style={{ color, fontSize: 12, fontWeight: 600 }}>{fmtSeconds(s)}</span>;
      },
    },
    {
      title: "Referrer",
      dataIndex: "referrer",
      width: 190,
      ellipsis: true,
      render: (ref, rec) => (
        <Space direction="vertical" size={0}>
          <Tag color={REFERRER_COLOR[rec.referrerType]} icon={REFERRER_ICON[rec.referrerType]} style={{ fontSize: 11 }}>
            {rec.referrerType || "direct"}
          </Tag>
          {ref && (
            <Tooltip title={ref}>
              <Text style={{ fontSize: 11, color: "#9ca3af" }}>
                {rec.referrerHost || ref.slice(0, 30)}
              </Text>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "IP",
      dataIndex: "ip",
      width: 115,
      render: (ip) => <Text style={{ fontSize: 12, color: "#6b7280" }}>{ip || "—"}</Text>,
    },
    {
      title: "",
      width: 50,
      render: (_, record) =>
        record.sessionId ? (
          <Tooltip title="View navigation path">
            <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => openPathModal(record)} />
          </Tooltip>
        ) : null,
    },
  ];

  const s = stats?.summary || {};

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <h3 style={{ margin: 0, fontWeight: 700 }}>User Activity</h3>
            <span style={{ color: "#6b7280" }}>Page visits, time spent, referrers & navigation paths</span>
          </div>
          <Button icon={<ReloadOutlined />} onClick={() => { load(); dispatch(fetchUserActivityStats(buildStatsP(filters))); }}>
            Refresh
          </Button>
        </div>

        {/* KPI cards */}
        <Row gutter={16} style={{ marginBottom: 20 }}>
          {[
            { title: "Total Page Views",  value: s.totalPageViews  ?? "—", icon: <FileSearchOutlined />, color: "#6366f1" },
            { title: "Unique Sessions",   value: s.uniqueSessions  ?? "—", icon: <GlobalOutlined />,     color: "#3b82f6" },
            { title: "Avg Time on Page",  value: s.avgTimeSpent != null ? fmtSeconds(s.avgTimeSpent) : "—", icon: <ClockCircleOutlined />, color: "#f59e0b" },
            { title: "Top Page",          value: stats?.topPages?.[0]?._id || "—",                         icon: <TeamOutlined />,        color: "#22c55e" },
          ].map(({ title, value, icon, color }) => (
            <Col span={6} key={title}>
              <Card size="small" style={{ borderRadius: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }} loading={statsLoading}>
                <Statistic
                  title={<span style={{ fontSize: 13, color: "#6b7280" }}>{title}</span>}
                  value={value}
                  prefix={React.cloneElement(icon, { style: { color } })}
                  valueStyle={{ fontSize: 20, fontWeight: 700 }}
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Charts row 1 */}
        <Row gutter={16} style={{ marginBottom: 20 }}>
          <Col span={12}>
            <Card size="small" title="Most Visited Pages" style={{ borderRadius: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }} loading={statsLoading}>
              <TopPagesBar data={stats?.topPages} />
            </Card>
          </Col>
          <Col span={12}>
            <Card size="small" title="Avg Time Spent per Page" style={{ borderRadius: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }} loading={statsLoading}>
              <AvgTimeBar data={stats?.avgTimePerPage} />
            </Card>
          </Col>
        </Row>

        {/* Charts row 2 */}
        <Row gutter={16} style={{ marginBottom: 20 }}>
          <Col span={8}>
            <Card size="small" title="Traffic Sources" style={{ borderRadius: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }} loading={statsLoading}>
              <ReferrerDonut data={stats?.referrerBreakdown} />
            </Card>
          </Col>
          <Col span={16}>
            <Card size="small" title="Top External Referrers" style={{ borderRadius: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }} loading={statsLoading}>
              <TopReferrersBar data={stats?.topReferrerHosts} />
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <div style={{
          background: "#fff", borderRadius: 10, padding: "14px 18px",
          boxShadow: "0 1px 6px rgba(0,0,0,0.06)", marginBottom: 16,
          display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center",
        }}>
          <Input
            placeholder="Search page, user, referrer..."
            prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
            style={{ width: 240 }}
            value={filters.search}
            onChange={(e) => updateFilter("search", e.target.value)}
            allowClear
          />
          <Select
            placeholder="Role"
            style={{ width: 145 }}
            allowClear
            value={filters.userRole}
            onChange={(v) => updateFilter("userRole", v)}
            options={[
              { label: "Super Admin",   value: "superadmin" },
              { label: "Company Admin", value: "companyAdmin" },
              { label: "Agent",         value: "user" },
            ]}
          />
          <Select
            placeholder="Traffic source"
            style={{ width: 145 }}
            allowClear
            value={filters.referrerType}
            onChange={(v) => updateFilter("referrerType", v)}
            options={[
              { label: "Direct",   value: "direct" },
              { label: "Internal", value: "internal" },
              { label: "External", value: "external" },
            ]}
          />
          <RangePicker style={{ width: 240 }} onChange={handleDateRange} disabledDate={(d) => d && d > dayjs().endOf("day")} />
          <Button
            onClick={() => {
              const reset = { page: 1, limit: 50, userRole: undefined, referrerType: undefined, search: "", startDate: undefined, endDate: undefined };
              setFilters(reset);
              dispatch(fetchUserActivity({ page: 1, limit: 50 }));
              dispatch(fetchUserActivityStats({}));
            }}
          >
            Reset
          </Button>
          <span style={{ marginLeft: "auto", color: "#6b7280", fontSize: 13 }}>{total.toLocaleString()} records</span>
        </div>

        {/* Table */}
        <div style={{ background: "#fff", borderRadius: 10, padding: "0 0 8px", boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>
          <Table
            columns={columns}
            dataSource={activities}
            rowKey="_id"
            loading={loading}
            size="small"
            scroll={{ x: 1100 }}
            pagination={{
              current: filters.page,
              pageSize: filters.limit,
              total,
              showSizeChanger: true,
              pageSizeOptions: ["25", "50", "100"],
              showTotal: (t, [from, to]) => `${from}–${to} of ${t}`,
              onChange: handlePageChange,
            }}
          />
        </div>

        {/* Navigation Path Modal */}
        <Modal
          title={
            <Space>
              <SwapRightOutlined />
              <Text strong>Navigation Path — {pathModal?.userName || "User"}</Text>
            </Space>
          }
          open={!!pathModal}
          onCancel={() => setPathModal(null)}
          footer={null}
          width={600}
        >
          {sessionPathLoading ? (
            <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>Loading path...</div>
          ) : sessionPath.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>No path data</div>
          ) : (
            <div style={{ maxHeight: 500, overflowY: "auto", padding: "8px 0" }}>
              <Steps
                direction="vertical"
                size="small"
                current={sessionPath.length}
                items={sessionPath.map((step, i) => ({
                  title: (
                    <Space>
                      <Text style={{ fontFamily: "monospace", fontSize: 13 }}>{step.page}</Text>
                      <Tag color="default" style={{ fontSize: 11 }}>{fmtSeconds(step.timeSpentSeconds)}</Tag>
                    </Space>
                  ),
                  description: (
                    <Space size={4} style={{ fontSize: 11, color: "#9ca3af" }}>
                      <span>{dayjs(step.enteredAt).format("HH:mm:ss")}</span>
                      {step.referrer && (
                        <>
                          <span>·</span>
                          <span>from {(() => {
                            if (step.referrer.startsWith("/")) return step.referrer;
                            try { return new URL(step.referrer).hostname; } catch { return step.referrer; }
                          })()}</span>
                        </>
                      )}
                    </Space>
                  ),
                  status: i === sessionPath.length - 1 ? "process" : "finish",
                }))}
              />
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
}
